import { Tooltip } from 'primereact/tooltip';
import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { FlexContainer } from '../styled-ui';

const StyledCard = styled(FlexContainer)<{
  color: string;
  backgroundColor: string;
  borderColor: string;
  zoomLevel: number;
}>`
  align-items: flex-start;
  justify-content: flex-start;
  flex: 1 1 20%;
  color: ${({ color }) => color};
  background-color: ${({ backgroundColor }) => `${backgroundColor}`};
  border: 1px solid var(--gray-dark);
  border-color: ${({ borderColor }) => borderColor};
  border-radius: var(--xsmall-border-radius);
  padding: var(--default-padding);
  width: 100%;
  min-width: ${({ zoomLevel }) => (zoomLevel > 1.25 ? '180px' : '224px')};
  flex-direction: column;
  overflow: hidden;

  p {
    margin: 0;
  }

  p.value {
    width: 100%;
    font-size: var(--card-value-font-size);
    line-height: var(--card-value-line-height);
    font-weight: 700;
    padding-top: var(--small-padding);
    padding-bottom: var(--small-padding);
    margin-top: auto;
  }

  .description {
    width: 100%;
    padding-top: var(--small-padding);
    font-size: var(--xxsmall-font-size);
    line-height: var(--xxsmall-line-height);
    border-top: 1px solid ${({ borderColor }) => borderColor};
    max-height: 42px;
  }

  @media (max-width: 1367px) {
    min-width: 180px;
  }
`;

const StyledCardHeader = styled(FlexContainer)<{ zoomLevel: number }>`
  justify-content: flex-start;
  align-items: ${({ zoomLevel }) =>
    zoomLevel > 1.25 ? 'flex-start' : 'center'};
  gap: var(--small-padding);

  p {
    font-size: ${({ zoomLevel }) =>
      zoomLevel > 1.25 ? 'var(-small-font-size)' : 'var(--medium-font-size)'};
    line-height: ${({ zoomLevel }) =>
      zoomLevel > 1.25
        ? 'var(--small-line-height)'
        : 'var(--medium-line-height)'};
  }

  @media (max-width: 1367px) {
    p {
      font-size: var(--small-font-size);
      line-height: var(--small-line-height);
    }
  }
`;

const IconContainer = styled(FlexContainer)`
  justify-content: center;
  align-items: center;
  min-width: 38px;
  max-width: 38px;
  min-height: 38px;
  max-height: 38px;
  border-radius: var(--xsmall-border-radius);
  background-color: var(--green-radar-light);
  color: var(--green-chart-middle);
`;

type PhishingDashboardCardProps = {
  id: string;
  color: string;
  backgroundColor: string;
  borderColor: string;
  title: string;
  icon: ReactNode;
  value: number;
  description: string;
  isPercentage?: boolean;
  numberTooltip?: string;
  descriptionTooltip?: string;
};

export const PhishingDashboardCard: React.FC<PhishingDashboardCardProps> = ({
  id,
  color,
  backgroundColor,
  borderColor,
  title,
  icon,
  value,
  description,
  isPercentage = false,
  numberTooltip,
  descriptionTooltip,
}) => {
  const zoomLevel = window.devicePixelRatio || 1;
  return (
    <>
      <StyledCard
        id={id}
        color={color}
        backgroundColor={backgroundColor}
        borderColor={borderColor}
        zoomLevel={zoomLevel}
      >
        <StyledCardHeader zoomLevel={zoomLevel}>
          <IconContainer>{icon}</IconContainer>
          <p>{title}</p>
        </StyledCardHeader>
        <p
          className="value"
          id={`${id}-number`}
          style={{ cursor: numberTooltip ? 'pointer' : undefined }}
        >
          {isPercentage ? `${value.toFixed(0)}%` : value}
        </p>
        <p
          className="description"
          id={`${id}-desc`}
          style={{ cursor: descriptionTooltip ? 'pointer' : undefined }}
        >
          {description}
        </p>
      </StyledCard>
      {numberTooltip && (
        <Tooltip
          target={`#${id}-number`}
          position="right"
          my="left center"
          at="right+10 center"
          content={numberTooltip}
          className="phishing-stat-tooltip"
          pt={{ root: { dir: document.documentElement.dir || 'ltr' } }}
        />
      )}
      {descriptionTooltip && (
        <Tooltip
          target={`#${id}-desc`}
          position="right"
          my="left center"
          at="right+10 center"
          content={descriptionTooltip}
          className="phishing-stat-tooltip"
          pt={{ root: { dir: document.documentElement.dir || 'ltr' } }}
        />
      )}
    </>
  );
};
